import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { translations, LANGS, DEFAULT_LANG } from "@/i18n/translations";

const LangContext = createContext(null);
const STORAGE_KEY = "beoasis.lang";

function detectLang() {
  // 1) URL prefix wins: /pt, /en, /de
  try {
    const first = window.location.pathname.split("/").filter(Boolean)[0];
    if (first && translations[first]) return first;
  } catch (e) {
    /* ignore */
  }
  // 2) previous choice from localStorage (persists user selection)
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) return saved;
  } catch (e) {
    /* ignore */
  }
  // 3) fall back to German (Swiss market default)
  return DEFAULT_LANG;
}

export function LangProvider({ children }) {
  const [lang, setLang] = useState(DEFAULT_LANG);

  // Detect on mount (client only to avoid SSR mismatch)
  useEffect(() => {
    setLang(detectLang());
  }, []);

  // Keep <html lang> in sync + persist
  useEffect(() => {
    try {
      document.documentElement.setAttribute("lang", lang);
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      /* ignore */
    }
  }, [lang]);

  const t = useCallback(
    (path) => {
      const parts = path.split(".");
      let cur = translations[lang] || translations[DEFAULT_LANG];
      for (const p of parts) {
        if (cur == null) return path;
        cur = cur[p];
      }
      return cur == null ? path : cur;
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, langs: LANGS }),
    [lang, t]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT() {
  const ctx = useContext(LangContext);
  if (!ctx) {
    // Fallback for testing / non-wrapped usage
    return {
      lang: DEFAULT_LANG,
      setLang: () => {},
      langs: LANGS,
      t: (path) => path,
    };
  }
  return ctx;
}
